/**
 * FG Magnit — iPhone showcase.
 * Model buttons filter the row (aria-pressed shows which is on); the arrows
 * scroll the row by about one view and disable themselves at either end.
 */
class FgIphone extends HTMLElement {
  connectedCallback() {
    this.row = this.querySelector('.fg-iph__row');
    this.cards = [...this.querySelectorAll('.fg-iph__card:not([data-mq-copy])')];
    this.marquee = this.querySelector('fg-marquee');
    this.bound = new WeakSet();
    this.filters = [...this.querySelectorAll('.fg-iph__filter')];
    this.arrows = [...this.querySelectorAll('.fg-iph__arrow')];
    if (!this.row) return;

    this.filters.forEach((btn) => btn.addEventListener('click', () => this.filter(btn)));
    this.arrows.forEach((btn) =>
      btn.addEventListener('click', () => {
        // When the row auto-scrolls, the arrows glide it one phone at a time.
        if (this.looping()) {
          this.marquee.step(Number(btn.dataset.dir));
          return;
        }
        const step = this.row.clientWidth * 0.85 * Number(btn.dataset.dir);
        this.row.scrollBy({ left: step, behavior: this.motion() });
      })
    );

    // The auto-scrolling row adds copies of the cards; give their colour dots
    // and hover colour-cycling the same behaviour as the originals.
    this.addEventListener('fg-mq:layout', () => {
      this.querySelectorAll('.fg-iph__card[data-mq-copy]').forEach((card) => {
        if (!this.bound.has(card)) this.bindColours(card);
      });
      this.updateArrows();
    });

    this.onScroll = () => {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        this.updateArrows();
      });
    };
    this.row.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.updateArrows();
    this.cards.forEach((card) => this.bindColours(card));
  }

  /**
   * Colour dots on a card: pointing at one previews that colour's photo, clicking
   * locks it and points every link on the card at that exact variant.
   */
  bindColours(card) {
    // Remembered per element (an attribute would be copied along with the card).
    this.bound = this.bound || new WeakSet();
    this.bound.add(card);
    const img = card.querySelector('[data-fg-img]');
    const dots = [...card.querySelectorAll('.fg-iph__dot[data-src]')];
    if (!img || dots.length < 2) return;
    const text = card.querySelector('[data-fg-colour-text]');
    const links = [...card.querySelectorAll('[data-fg-link]')];
    let locked = dots.find((d) => d.classList.contains('is-active')) || dots[0];

    const show = (dot, lock) => {
      if (img.dataset.current !== dot.dataset.src) {
        img.classList.add('is-swapping');
        const next = new Image();
        next.onload = () => {
          img.srcset = dot.dataset.src;
          img.src = dot.dataset.src;
          img.dataset.current = dot.dataset.src;
          img.classList.remove('is-swapping');
        };
        next.src = dot.dataset.src;
      }
      if (text) text.textContent = dot.dataset.colour;
      if (lock) {
        locked = dot;
        dots.forEach((d) => {
          d.classList.toggle('is-active', d === dot);
          d.setAttribute('aria-pressed', String(d === dot));
        });
        links.forEach((a) => (a.href = dot.dataset.url));
      }
    };

    dots.forEach((dot) => {
      dot.addEventListener('pointerenter', () => {
        stopCycle();
        show(dot, false);
      });
      dot.addEventListener('focus', () => show(dot, false));
      dot.addEventListener('click', () => {
        stopCycle();
        show(dot, true);
      });
    });
    const group = card.querySelector('[data-fg-colours]');
    group.addEventListener('pointerleave', () => show(locked, false));
    group.addEventListener('focusout', (e) => {
      if (!group.contains(e.relatedTarget)) show(locked, false);
    });

    // Hovering the photo walks through every colour the phone comes in,
    // so the whole range is seen without a click. Leaving returns to the
    // chosen colour. Skipped when the visitor prefers reduced motion.
    let timer = null;
    const stopCycle = () => {
      if (timer) clearInterval(timer);
      timer = null;
      card.classList.remove('is-cycling');
    };
    const media = card.querySelector('.fg-iph__media');
    media.addEventListener('pointerenter', (e) => {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      stopCycle();
      card.classList.add('is-cycling');
      let i = dots.indexOf(locked);
      const step = () => {
        i = (i + 1) % dots.length;
        show(dots[i], false);
      };
      timer = setInterval(step, 1100);
      setTimeout(() => timer && step(), 350);
    });
    media.addEventListener('pointerleave', () => {
      stopCycle();
      show(locked, false);
    });
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onScroll);
  }

  motion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  filter(button) {
    const value = button.dataset.filter;
    this.filters.forEach((btn) => btn.setAttribute('aria-pressed', String(btn === button)));
    this.cards.forEach((card) => {
      const models = (card.dataset.model || '').split(' ');
      card.hidden = value !== 'all' && !models.includes(value);
    });
    if (this.looping()) {
      this.marquee.refresh();
    } else {
      this.row.scrollTo({ left: 0, behavior: 'auto' });
    }
    this.updateArrows();
  }

  looping() {
    return !!(this.marquee && this.marquee.classList.contains('is-live') && this.marquee.setWidth);
  }

  updateArrows() {
    const [prev, next] = this.arrows;
    if (!prev || !next) return;
    if (this.looping()) {
      // An endless row: both arrows always work.
      prev.disabled = false;
      next.disabled = false;
      this.classList.remove('fg-iph--static');
      this.classList.add('fg-iph--looping');
      return;
    }
    const max = this.row.scrollWidth - this.row.clientWidth;
    prev.disabled = this.row.scrollLeft <= 2;
    next.disabled = this.row.scrollLeft >= max - 2;
    this.classList.toggle('fg-iph--static', max <= 2);
  }
}

if (!customElements.get('fg-iphone')) {
  customElements.define('fg-iphone', FgIphone);
}
